import { useMemo, useRef } from "react";

import dayjs from "dayjs";

import { LineGraph } from "@similarweb/pro-logic-components";
import { EmptyState } from "@similarweb/ui-components";
import { Stack, Box, Flex } from "@similarweb/ui-components/foundations";

import type { ILegendItem } from "app/components/React/Legends/legendsTypes";
import type { granularityItem } from "app/components/widget/widget-utilities/time-granularity";
import { useCategoriesComparison } from "app/pages/industry-analysis/categories-context/CategoriesContext";
import { useTranslation } from "pro-features/components/WithTranslation";

import type { CategoryComparisonData, MetricKey } from "../types";

import { HighLevelMetricsComparisonChartHeader } from "./HighLevelMetricsComparisonChartHeader";
import { processStructuredChartData, getYAxisFormatter } from "./utils/dataProcessor";
import { getPopTooltipConfig } from "./utils/getPopTooltipConfig";
import { getTooltipConfig } from "./utils/getTooltipConfig";

type HighLevelMetricsComparisonChartProps = {
    categoriesData: CategoryComparisonData[];
    isPeriodOverPeriod: boolean;
    selectedMetric: MetricKey;
    timeGranularity: granularityItem;
    legendItems: ILegendItem[];
    toggleLegendItem: (legendItem: ILegendItem) => void;
    onGranularityChange: (selectedIndex: number) => void;
};

export const HighLevelMetricsComparisonChart = ({
    categoriesData,
    isPeriodOverPeriod,
    selectedMetric,
    timeGranularity,
    legendItems,
    toggleLegendItem,
    onGranularityChange,
}: HighLevelMetricsComparisonChartProps) => {
    const t = useTranslation();
    const chartRef = useRef<HTMLDivElement>(null);
    const { categories } = useCategoriesComparison();

    const chartData = useMemo(() => {
        const visibleCategoryIds = new Set(legendItems.filter((item) => item.visible).map((item) => item.id));
        return processStructuredChartData(
            categoriesData,
            selectedMetric,
            timeGranularity.value,
            visibleCategoryIds,
            categories,
        );
    }, [categoriesData, selectedMetric, timeGranularity.value, legendItems, categories]);

    const yAxisFormatter = getYAxisFormatter(selectedMetric);

    return (
        <Stack>
            <HighLevelMetricsComparisonChartHeader
                selectedMetric={selectedMetric}
                timeGranularity={timeGranularity}
                legendItems={legendItems}
                chartElement={chartRef.current}
                onLegendToggle={toggleLegendItem}
                onGranularityChange={onGranularityChange}
                isPeriodOverPeriod={isPeriodOverPeriod}
            />

            {chartData?.length > 0 ? (
                <Box ref={chartRef}>
                    <LineGraph
                        withMarkers
                        data={chartData}
                        keepSameLegendsOnDataUpdate={false}
                        shouldPresentLegend={false}
                        graphConfig={{
                            chart: {
                                height: 280,
                            },
                            tooltip: isPeriodOverPeriod
                                ? getPopTooltipConfig(selectedMetric, timeGranularity.value)
                                : getTooltipConfig(selectedMetric, timeGranularity.value),
                            xAxis: {
                                type: "datetime",
                                labels: {
                                    formatter: function ({ value }) {
                                        return dayjs.utc(value).format(timeGranularity.format);
                                    },
                                },
                            },
                            yAxis: {
                                labels: {
                                    formatter: function ({ value }) {
                                        return yAxisFormatter(Number(value));
                                    },
                                },
                            },
                        }}
                    />
                </Box>
            ) : (
                <Flex
                    height="280px"
                    justifyContent="center"
                    alignItems="center"
                >
                    <EmptyState title={t("global.nodata.notavilable")} />
                </Flex>
            )}
        </Stack>
    );
};
